import React, { useState, useEffect } from "react";
import Sidebar from "../../../components/Dashboard/Sidebar/Sidebar";
import Header from "../../../components/Dashboard/Header/Header";
import {
    MapPin,
    Clock,
    Loader2,
    CheckCircle2,
    Navigation,
    AlertCircle,
    Clock9,
    Building2,
    ShieldCheck,
    ChevronRight,
    Search
} from 'lucide-react';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Pagination from '../../../components/Common/Pagination/Pagination';
import styles from './MyAttendance.module.css';
import Footer from '../../../components/Common/Footer/Footer';
import api from "../../../api/axios";
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const MyAttendance = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [isPunching, setIsPunching] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total: 0,
        per_page: 12
    });

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    // Fetch projects and active session
    const fetchData = async (page = pagination.current_page) => {
        setLoading(true);
        try {
            const [projectsRes, sessionRes] = await Promise.all([
                api.get('/employee-assigned-projects', {
                    params: {
                        page: page,
                        per_page: pagination.per_page
                    }
                }),
                api.get('/visit-sessions', {
                    params: {
                        employee_id: user.employee_id || user.id
                    }
                })
            ]);

            if (projectsRes.data.success) {
                // Handling paginated response from /projects
                const paginatedData = projectsRes.data.data;
                setProjects(paginatedData.data || []);
                setPagination(prev => ({
                    ...prev,
                    current_page: paginatedData.current_page,
                    total: paginatedData.total
                }));
            }

            const sessionItems = sessionRes.data?.data?.data || sessionRes.data?.data || [];
            if (Array.isArray(sessionItems) && sessionItems.length > 0) {
                // Find the first session without a punch-out time
                const openSession = sessionItems.find(s => !s.punch_out);
                setActiveSession(openSession || null);
            } else {
                setActiveSession(null);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load project data');
        } finally {
            setLoading(false);
        }
    };

    // Get user location
    const getPosition = () => {
        if (!navigator.geolocation) {
            setLocationError("Geolocation is not supported by your browser");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                });
                setLocationError(null);
            },
            (error) => {
                console.error('Location error:', error);
                setLocationError("Location access denied. Please enable GPS to punch in.");
            },
            { enableHighAccuracy: true }
        );
    };

    useEffect(() => {
        fetchData();
        getPosition();
        // Periodically update location
        const interval = setInterval(getPosition, 10000);
        return () => clearInterval(interval);
    }, []);

    // Haversine formula to calculate distance in meters
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;

        const R = 6371e3; // Earth's radius in meters
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // Distance in meters
    };

    const currentMatchedProject = React.useMemo(() => {
        if (!userLocation || projects.length === 0) return null;
        let nearest = null;
        let minDistance = Infinity;
        projects.forEach(p => {
            const distance = calculateDistance(
                userLocation.lat,
                userLocation.lng,
                parseFloat(p.site_latitude),
                parseFloat(p.site_longitude)
            );
            const allowedRadius = p.radius || 200;
            if (distance <= allowedRadius && distance < minDistance) {
                minDistance = distance;
                nearest = p;
            }
        });
        return nearest;
    }, [userLocation, projects]);

    const activeDisplayProject = activeSession ? (currentMatchedProject || activeSession.project || {
        id: activeSession.project_id,
        project_name: activeSession.project_name || 'Current Site',
        name: activeSession.project_name || 'Current Site'
    }) : null;

    const handlePunchIn = async (project) => {
        if (!userLocation) {
            toast.error("Location not available. Please wait...");
            return;
        }

        console.log(locationError, userLocation, "locationerror")

        const confirm = await MySwal.fire({
            title: '<span style="font-family: \'Outfit\', sans-serif; font-weight: 800; font-size: 24px; color: #0f172a;">Punch In?</span>',
            html: `
                <div style="text-align: center; font-family: 'Outfit', sans-serif;">
                    <p style="margin-bottom: 8px; color: #475569; font-size: 15px; font-weight: 500;">
                        Are you sure you want to clock in at:
                    </p>
                    <p style="color: #f97316; font-size: 18px; font-weight: 800; margin: 0;">${project.project_name}</p>
                </div>
            `,
            icon: 'question',
            iconColor: '#f97316',
            showCancelButton: true,
            confirmButtonText: 'Yes, Punch In',
            cancelButtonText: 'Cancel',
            customClass: {
                popup: styles['swal-premium-popup'],
                confirmButton: styles['swal-confirm-btn'],
                cancelButton: styles['swal-cancel-btn']
            },
            buttonsStyling: false
        });

        if (confirm.isConfirmed) {
            setIsPunching(true);
            try {
                await api.post('/visit-sessions/punch-in', {
                    employee_id: user.employee_id || user.id,
                    project_id: project.id,
                    lat: userLocation.lat,
                    lng: userLocation.lng,
                    location_name: project.site_address
                });
                toast.success(`Successfully punched in at ${project.project_name}`);
                fetchData();
            } catch (error) {
                console.error('Punch in error:', error);
                toast.error(error.response?.data?.message || 'Punch in failed');
            } finally {
                setIsPunching(false);
            }
        }
    };

    const handlePunchOut = async () => {
        if (!userLocation || !activeSession) {
            toast.error("Location or active session not available.");
            return;
        }

        const project = activeDisplayProject;

        const confirm = await MySwal.fire({
            title: '<span style="font-family: \'Outfit\', sans-serif; font-weight: 800; font-size: 24px; color: #0f172a;">Punch Out?</span>',
            html: `
                <div style="text-align: center; font-family: 'Outfit', sans-serif;">
                    <p style="margin-bottom: 0; color: #475569; font-size: 15px; font-weight: 500;">
                        Are you sure you want to clock out for the day?
                    </p>
                    <p style="color: #64748b; font-size: 13px; margin-top: 8px;">This will end your current session at <b>${project.project_name || project.name}</b>.</p>
                </div>
            `,
            icon: 'warning',
            iconColor: '#ef4444',
            showCancelButton: true,
            confirmButtonText: 'Yes, Punch Out',
            cancelButtonText: 'Cancel',
            customClass: {
                popup: styles['swal-premium-popup'],
                confirmButton: styles['swal-danger-btn'],
                cancelButton: styles['swal-cancel-btn']
            },
            buttonsStyling: false
        });

        if (confirm.isConfirmed) {
            setIsPunching(true);
            try {
                await api.post('/visit-sessions/punch-out', {
                    employee_id: user.employee_id || user.id,
                    project_id: project.id,
                    lat: userLocation.lat,
                    lng: userLocation.lng
                });
                toast.success(`Successfully punched out from ${project.project_name}`);
                fetchData();
            } catch (error) {
                console.error('Punch out error:', error);
                toast.error(error.response?.data?.message || 'Punch out failed');
            } finally {
                setIsPunching(false);
            }
        }
    };

    return (
        <div className={styles['attendance-container']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={toggleSidebar} />
                <main className={styles['main-content']}>
                    <Breadcrumbs customItems={[
                        { label: 'Employee Dashboard', path: '/employees/dashboard' },
                        { label: 'Attendance', path: '/employees/my-attendance' }
                    ]} />

                    <div className={styles['page-header']}>
                        <div className={styles['header-title']}>
                            <h1>Attendance</h1>
                            <p>Punch in/out based on your current project site.</p>
                        </div>
                        {userLocation && (
                            <div className={styles['location-badge']}>
                                <Navigation size={14} />
                                <span>GPS Active</span>
                            </div>
                        )}
                    </div>

                    {locationError && (
                        <div className={styles['error-banner']}>
                            <AlertCircle size={20} />
                            <span>{locationError}</span>
                        </div>
                    )}

                    {activeSession && activeDisplayProject && (
                        <div className={styles['active-session-banner']}>
                            <div className={styles['session-info']}>
                                <div className={styles['pulse-dot']}></div>
                                <div>
                                    <h3>Currently Working at {activeDisplayProject.project_name || activeDisplayProject.name}</h3>
                                    <p>Started at {new Date(activeSession.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                </div>
                            </div>
                            <button
                                className={styles['punch-out-btn']}
                                onClick={handlePunchOut}
                                disabled={isPunching || !userLocation}
                            >
                                {isPunching ? <Loader2 className={styles['spinner']} /> : <Clock size={20} />}
                                <span>{!userLocation && !locationError ? 'Locating...' : 'Punch Out'}</span>
                            </button>
                        </div>
                    )}

                    <div className={styles['projects-grid']}>
                        {loading ? (
                            <div className={styles['loading-state']}>
                                <Loader2 size={40} className={styles['spinner']} />
                                <p>Checking available sites...</p>
                            </div>
                        ) : projects.length > 0 ? (
                            projects.map((project) => {
                                const distance = calculateDistance(
                                    userLocation?.lat,
                                    userLocation?.lng,
                                    parseFloat(project.site_latitude),
                                    parseFloat(project.site_longitude)
                                );

                                const allowedRadius = project.radius || 200; // Default 200 meters
                                const isWithinRadius = distance <= allowedRadius;
                                const isThisActive = activeSession && activeDisplayProject?.id === project.id;

                                return (
                                    <div key={project.id} className={`${styles['project-card']} ${isWithinRadius ? styles['nearby'] : ''}`}>
                                        <div className={styles['card-main']}>
                                            <div className={styles['project-icon-box']}>
                                                <Building2 size={24} />
                                            </div>
                                            <div className={styles['project-details']}>
                                                <h3>{project.project_name}</h3>
                                                <div className={styles['project-meta']}>
                                                    <span className={styles['code-badge']}>{project.project_code}</span>
                                                    <span className={styles['distance-text']}>
                                                        {userLocation ? (
                                                            distance > 1000
                                                                ? `${(distance / 1000).toFixed(1)} km away`
                                                                : `${Math.round(distance)} meters away`
                                                        ) : 'Detecting location...'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles['card-actions']}>
                                            {isWithinRadius ? (
                                                isThisActive ? (
                                                    <div className={styles['status-active']}>
                                                        <CheckCircle2 size={16} />
                                                        <span>Current session</span>
                                                    </div>
                                                ) : !activeSession ? (
                                                    <button
                                                        className={styles['punch-btn']}
                                                        onClick={() => handlePunchIn(project)}
                                                        disabled={isPunching}
                                                    >
                                                        {isPunching ? <Loader2 className={styles['spinner']} /> : <ShieldCheck size={18} />}
                                                        Punch In
                                                    </button>
                                                ) : (
                                                    <span className={styles['status-locked']}>Other session active</span>
                                                )
                                            ) : (
                                                <div className={styles['out-of-range']}>
                                                    <MapPin size={16} />
                                                    <span>Out of Range</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles['no-projects']}>
                                <AlertCircle size={40} />
                                <p>No project sites found near you.</p>
                            </div>
                        )}
                    </div>

                    <div className={styles['desktop-pagination']}>
                        <Pagination
                            currentPage={pagination.current_page}
                            totalItems={pagination.total}
                            itemsPerPage={pagination.per_page}
                            onPageChange={(page) => fetchData(page)}
                        />
                    </div>

                    <div className={styles['mobile-pagination']}>
                        <Pagination
                            currentPage={pagination.current_page}
                            totalItems={pagination.total}
                            itemsPerPage={pagination.per_page}
                            onPageChange={(page) => fetchData(page)}
                        />
                    </div>

                    <Footer />
                </main>
            </div>
        </div>
    );
};

export default MyAttendance;
